import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from 'src/app/api.service';
import { ThemingService } from 'src/app/theming.service';
import { AppComponent } from 'src/app/app.component';
import { LambdaService } from 'src/app/lambda.service';
declare var $: any;

@Component({
  selector: 'app-edit-menu',
  templateUrl: './edit-menu.component.html',
  styleUrls: ['./edit-menu.component.scss']
})
export class EditMenuComponent implements OnInit {
  @Input() menuId!: number;
  @Output() close = new EventEmitter<void>();

  menu: any = {
    menuName: '',
    menuStatus: 0
  };
  menuOptions: any[] = [];
  categories: any[] = [];
  collections: any[] = [];
  subCategories: any[] = [];
  subSubCategories: any[] = [];
  components: any[] = [];
  pages: any[] = [];
  loading: boolean = false;
  deletedMenuOptionIds: number[] = [];
  itemListId: string = '';

  constructor(
    private route: ActivatedRoute,
    private apiService: ApiService,
    private themingService: ThemingService,
    public appComponent: AppComponent,
    private cdr: ChangeDetectorRef,
    private lambdaService: LambdaService
  ) {
    this.route.paramMap.subscribe((params) => {
      let menuId = Number(params.get('menuId'));
      if (menuId !== this.menuId) {
        this.appComponent.goToTop();
        this.menuId = menuId;
      }
    });
  }

  ngOnInit(): void {
      this.cdr.detectChanges();
      this.getCategories(),
      this.getCollections(),
      this.getComponents(),
      this.getSubCategories(),
      this.getSubSubCategories(),
      this.getPages(),
      this.getMenuById(this.menuId);
  }

  async getMenuById(id: number): Promise<void> {
    if (!id) return;
    this.loading = true;

    try {
      const data: any = await this.lambdaService.getMenuById(id, this.themingService.shopId);
      if (data.menu?.length) this.menu = data.menu[0];
      const map = new Map<number, any>();
      const roots: any[] = [];
      data.menu_options.forEach((r: any) => {
        const node = {
          optionType   : this.getOptionType(r),
          optionName   : r.menuOptionName,
          optionId     : r.menuOptionId,
          categoryId   : r.categoryId,
          subCategoryId: r.subCategoryId,
          subSubCategoryId: r.subSubCategoryId,
          collectionId : r.collectionId,
          optionUrl    : r.menuOptionUrl,
          position     : r.menuOptionPosition,
          componentId  : r.componentId,
          pageId       : r.pageId,
          parentOptionId: r.parentOptionId,
          children     : [] as any[]
        };
        map.set(node.optionId, node);
        (r.parentOptionId === null ? roots : map.get(r.parentOptionId)?.children)?.push(node);
      });
      const sortByPos = (arr: any[]) => {
        arr.sort((a, b) => a.position - b.position);
        arr.forEach(c => sortByPos(c.children));
      };
      sortByPos(roots);

      this.menuOptions = roots;
    } catch (e) {
      console.error(e);
    } finally {
      this.loading = false;
    }
  }

  async getPages(): Promise<void> {
    this.loading = true;
    try {
      const response: any = await this.lambdaService.getPages(this.themingService.shopId);
      const pageRows = Array.isArray(response.data) ? response.data : [];
      this.pages = pageRows;
    } catch (error) {
      console.error('Error fetching pages:', error);
    } finally {
      this.loading = false;
    }
  }

  async getCategories(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getCategories(this.themingService.shopId);
      const categoryRows = Array.isArray(response[0]) ? response[0] : [];
      this.categories = categoryRows;
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }

  async getCollections(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getCollections(this.themingService.shopId);
      this.collections = Array.isArray(response.collections) ? response.collections : [];
    } catch (error) {
      console.error('Error fetching collections:', error);
      this.collections = [];
    }
  }

  async getSubCategories(): Promise<void> {
    this.loading = true;
    try {
      const response: any = await this.lambdaService.getSubCategories(this.themingService.shopId);
      this.subCategories = Array.isArray(response.subCategories) ? response.subCategories : [];
    } catch (error) {
      console.error('Error fetching subCategories:', error);
    } finally {
      this.loading = false;
    }
  }

  async getSubSubCategories(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getSubSubCategories(this.themingService.shopId);
      const subSubCategoryRows = Array.isArray(response.subSubCategories) ? response.subSubCategories : [];
      this.subSubCategories = subSubCategoryRows;
    } catch (error) {
      console.error('Error fetching subSubCategories:', error);
    }
  }

  async getComponents(): Promise<void> {
    this.loading = true;
    try {
      const response: any = await this.lambdaService.getComponents(this.themingService.shopId);
      const componentRows = Array.isArray(response[0]) ? response[0] : [];
      this.components = componentRows;
    } catch (error) {
      console.error('Error fetching components:', error);
    } finally {
      this.loading = false;
    }
  }

  getOptionType(option: any): string {
    switch (option.menuOptionType) {
      case 1: return 'categories';
      case 2: return 'subCategories';
      case 3: return 'subSubcategories';
      case 4: return 'collections';
      case 5: return 'custom';
      case 6: return 'components';
      case 7: return 'pages';
      default: return '';
    }
  }

  addMenuOption() {
    this.menuOptions.push({
      optionType: '',
      optionName: '',
      optionId: 0,
      categoryId: 0,
      subCategoryId: 0,
      subSubCategoryId: 0,
      collectionId: 0,
      optionUrl: '',
      position: this.menuOptions.length + 1,
      componentId: 0,
      pageId: 0
    });
  }

  removeMenuOption(index: number) {
    const optionId = this.menuOptions[index].optionId;
    if (optionId !== 0) {
      this.deletedMenuOptionIds.push(optionId);
    }
    this.menuOptions.splice(index, 1);
    this.menuOptions.forEach((option, i) => {
      option.position = i + 1;
    });
  }

  onOptionChange(option: string, index: number) {
    this.menuOptions[index].categoryId = 0;
    this.menuOptions[index].subCategoryId = 0;
    this.menuOptions[index].subSubCategoryId = 0;
    this.menuOptions[index].collectionId = 0;
    this.menuOptions[index].optionUrl = '';
    this.menuOptions[index].componentId = 0;
    this.menuOptions[index].pageId = 0;

    switch (option) {
      case 'categories':
        this.menuOptions[index].optionType = 'categories';
        break;
      case 'subCategories':
        this.menuOptions[index].optionType = 'subCategories';
        break;
      case 'subSubcategories':
        this.menuOptions[index].optionType = 'subSubcategories';
        break;
      case 'collections':
        this.menuOptions[index].optionType = 'collections';
        break;
      case 'custom':
        this.menuOptions[index].optionType = 'custom';
        break;
      case 'components':
        this.menuOptions[index].optionType = 'components';
        break;
      case 'pages':
        this.menuOptions[index].optionType = 'pages';
        break;
    }
  }

  ChangeMenuName(event: Event) {
    const element = event.target as HTMLInputElement;
    this.menu.menuName = element.value;
  }

  onCategoryChange(event: any, index: number) {
    const selectedCategory = this.categories.find(category => category.categoryId === +event.target.value);
    if (selectedCategory) {
      this.menuOptions[index].categoryId = selectedCategory.categoryId;
    }
  }

  onSubCategoryChange(event: any, index: number) {
    const selectedSubCategory = this.subCategories.find(subCategory => subCategory.subCategoryId === +event.target.value);
    if (selectedSubCategory) {
      this.menuOptions[index].subCategoryId = selectedSubCategory.subCategoryId;
    }
  }

  onSubSubCategoryChange(event: any, index: number) {
    const selectedSubSubCategory = this.subSubCategories.find(subSubCategory => subSubCategory.subSubCategoryId === +event.target.value);
    if (selectedSubSubCategory) {
      this.menuOptions[index].subSubCategoryId = selectedSubSubCategory.subSubCategoryId;
    }
  }

  onCollectionChange(event: any, index: number) {
    const selectedCollection = this.collections.find(collection => collection.collectionId === +event.target.value);
    if (selectedCollection) {
      this.menuOptions[index].collectionId = selectedCollection.collectionId;
    }
  }

  onComponentChange(event: any, index: number) {
    const selectedComponent = this.components.find(component => component.componentId === +event.target.value);
    if (selectedComponent) {
      this.menuOptions[index].componentId = selectedComponent.componentId;
      if (selectedComponent.categoryId) {
        this.menuOptions[index].categoryId = selectedComponent.categoryId;
      }
      if (selectedComponent.subCategoryId) {
        this.menuOptions[index].subCategoryId = selectedComponent.subCategoryId;
      }
      if (selectedComponent.subSubCategoryId) {
        this.menuOptions[index].subSubCategoryId = selectedComponent.subSubCategoryId;
      }
      if (selectedComponent.collectionId) {
        this.menuOptions[index].collectionId = selectedComponent.collectionId;
      }
    }
  }

  onPageChange(event: any, index: number) {
    const selectedPage = this.pages.find(page => page.pageId === +event.target.value);
    if (selectedPage) {
      this.menuOptions[index].pageId = selectedPage.pageId;
    }
  }

  onUrlChange(event: any, index: number) {
    this.menuOptions[index].optionUrl = event.target.value;
  }

  isValidHttpUrl(url: string): boolean {
    if (!url) return false;
    const pattern = /^https?:\/\/[\w\-]+(\.[\w\-]+)+([\/\w\-._~:?#[\]@!$&'()*+,;=]*)?$/;
    return pattern.test(url);
  }


  validateFormData() {
    // —————— 1. Nombre del menú ——————
    if (!this.menu.menuName?.trim()) {
      $('#requiredEditMenuModal').modal('show');
      return;
    }
    for (let i = 0; i < this.menuOptions.length; i++) {
      const opt = this.menuOptions[i];
      if (!opt.optionName?.trim() || !opt.optionType) {
        $('#requiredEditMenuModal').modal('show');
        return;
      }
      switch (opt.optionType) {
        case 'categories':
          if (!opt.categoryId) { $('#requiredEditMenuModal').modal('show'); return; }
          break;
        case 'subCategories':
          if (!opt.subCategoryId) { $('#requiredEditMenuModal').modal('show'); return; }
          break;
        case 'subSubcategories':
          if (!opt.subSubCategoryId) { $('#requiredEditMenuModal').modal('show'); return; }
          break;
        case 'collections':
          if (!opt.collectionId) { $('#requiredEditMenuModal').modal('show'); return; }
          break;
        case 'custom':
          if (!opt.optionUrl || !this.isValidHttpUrl(opt.optionUrl)) {
            this.itemListId = `${i + 1}`;
            $('#errorEditUrl').modal('show');
            return;
          }
          break;
        case 'components':
          if (!opt.componentId) { $('#requiredEditMenuModal').modal('show'); return; }
          break;
        case 'pages':
          if (!opt.pageId) { $('#requiredEditMenuModal').modal('show'); return; }
          break;
        default:
          $('#requiredEditMenuModal').modal('show');
          return;
      }
    }
    for (let i = 0; i < this.menuOptions.length; i++) {
      const parent = this.menuOptions[i];
      const children = parent.children || [];
      for (let j = 0; j < children.length; j++) {
        const child = children[j];
        if (!child.optionName?.trim() || !child.optionType) {
          this.itemListId = `S${i + 1}.${j + 1}`;
          $('#requiredEditMenuModal').modal('show');
          return;
        }
        switch (child.optionType) {
          case 'categories':
            if (!child.categoryId) { $('#requiredEditMenuModal').modal('show'); return; }
            break;
          case 'subCategories':
            if (!child.subCategoryId) { $('#requiredEditMenuModal').modal('show'); return; }
            break;
          case 'subSubcategories':
            if (!child.subSubCategoryId) { $('#requiredEditMenuModal').modal('show'); return; }
            break;
          case 'collections':
            if (!child.collectionId) { $('#requiredEditMenuModal').modal('show'); return; }
            break;
          case 'custom':
            if (!child.optionUrl || !this.isValidHttpUrl(child.optionUrl)) {
              this.itemListId = `S${i + 1}.${j + 1}`;
              $('#errorEditUrl').modal('show');
              return;
            }
            break;
          case 'components':
            if (!child.componentId) { $('#requiredEditMenuModal').modal('show'); return; }
            break;
          case 'pages':
            if (!child.pageId) { $('#requiredEditMenuModal').modal('show'); return; }
            break;
          default:
            $('#requiredEditMenuModal').modal('show');
            return;
        }
      }
    }
    this.updateMenu();
  }

  updateMenu() {
    this.loading = true;
    const flat: any[] = [];
    const walk = (node: any, parentId: number | null) => {
      flat.push({
        menuId          : this.menu.menuId,
        menuOptionId    : node.optionId,
        parentOptionId  : parentId !== null ? parentId : 0,
        menuOptionPosition: node.position,
        menuOptionName  : node.optionName,
        menuOptionUrl   : node.optionUrl,
        categoryId      : node.categoryId,
        subCategoryId   : node.subCategoryId,
        subSubCategoryId: node.subSubCategoryId,
        collectionId    : node.collectionId,
        menuOptionType  : this.getNumericOptionType(node.optionType),
        componentId     : node.componentId || 0,
        pageId          : node.pageId || 0
      });
      node.children?.forEach((c: any) => walk(c, node.optionId));
    };
    this.menuOptions.forEach(root => walk(root, null));
    this.lambdaService.updateMenu(
      this.themingService.shopId,
      this.menu.menuId,
      this.menu.menuName,
      this.menu.menuStatus,
      flat,
      this.deletedMenuOptionIds
    )
    .then((res: any) => {
      if (res.status === 200) {
        $('#successMenuModal').modal('show');
        this.getMenuById(this.menu.menuId);
      } else {
        $('#errorMenuModal').modal('show');
      }
      this.loading = false;
    })
    .catch(err => {
      console.error(err);
      this.loading = false;
      $('#errorMenuModal').modal('show');
    });
  }

  onSubUrlChange(event: any, parentIndex: number, childIndex: number): void {
    this.menuOptions[parentIndex]
        .children[childIndex]
        .optionUrl = event.target.value;
  }

  addSubMenuOption(parentIndex: number): void {
    const parent = this.menuOptions[parentIndex];
    if (!parent.children) parent.children = [];

    parent.children.push({
      optionType        : '',
      optionName        : '',
      optionId          : 0,
      categoryId        : 0,
      subCategoryId     : 0,
      subSubCategoryId  : 0,
      collectionId      : 0,
      optionUrl         : '',
      componentId       : 0,
      pageId            : 0,
      position          : parent.children.length + 1,
      children          : []
    });
  }

  removeSubMenuOption(parentIndex: number, childIndex: number): void {
    const children = this.menuOptions[parentIndex].children;
    if (!children) return;

    const optionId = children[childIndex].optionId;
    if (optionId !== 0) {
      this.deletedMenuOptionIds.push(optionId);
    }

    children.splice(childIndex, 1);
    children.forEach((c: any, idx: number) => (c.position = idx + 1));
  }

  getNumericOptionType(optionType: string): number {
    switch (optionType) {
      case 'categories':
        return 1;
      case 'subCategories':
        return 2;
      case 'subSubcategories':
        return 3;
      case 'collections':
        return 4;
      case 'custom':
        return 5;
      case 'components':
        return 6;
      case 'pages':
        return 7;
      default:
        return 0;
    }
  }

  closeModal(): void {
    $('#successMenuModal').modal('hide');
    $('#errorMenuModal').modal('hide');
    $('#requiredEditMenuModal').modal('hide');
    $('#errorEditUrl').modal('hide');
  }

  closeEdit(): void {
    this.close.emit();
  }
}
